import React from 'react';
import { motion } from 'framer-motion';
import { Calculator, TrendingUp, Scale, MessageCircle, Settings, ArrowRight, LayoutGrid, MapPin, Map, Smartphone } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';

interface Tool {
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<any>;
  route: string;
  color: string;
}

export const AvailableTools: React.FC = () => {
  const navigate = useNavigate();

  const tools: Tool[] = [
    {
      id: 'mobile-calculator',
      title: 'Mobile Calculator',
      description: 'Touch-optimized mortgage calculator',
      icon: Smartphone,
      route: '/calculator-mobile',
      color: 'hsl(280, 70%, 50%)'
    },
    {
      id: 'calculator',
      title: 'Large Format Calculator',
      description: 'Best views on desktops, laptops and tablets',
      icon: Calculator,
      route: '/calculator',
      color: 'hsl(var(--primary))'
    },
    {
      id: 'smartbuy',
      title: 'Smart Buy Analyzer',
      description: 'Compare buying scenarios and get AI recommendations',
      icon: TrendingUp,
      route: '/buying-power-enhanced',
      color: 'hsl(var(--chart-2))'
    },
    {
      id: 'comparison',
      title: 'Property Comparison',
      description: 'Compare multiple properties side by side',
      icon: Scale,
      route: '/compare',
      color: 'hsl(var(--chart-3))'
    },
    {
      id: 'support',
      title: 'C.L.A.R.A. Assistant',
      description: 'Get help and guidance',
      icon: MessageCircle,
      route: '/chatbot',
      color: 'hsl(var(--chart-4))'
    },
    {
      id: 'floor-plans',
      title: 'Floor Plans',
      description: 'Browse and manage floor plan templates',
      icon: LayoutGrid,
      route: '/floor-plans',
      color: 'hsl(var(--chart-5))'
    },
    {
      id: 'lots',
      title: 'Lot Inventory',
      description: 'View and manage available lots',
      icon: MapPin,
      route: '/lots',
      color: 'hsl(142, 76%, 36%)'
    },
    {
      id: 'plat-explorer',
      title: 'Plat Explorer',
      description: 'Explore subdivision plat maps',
      icon: Map,
      route: '/plat-explorer',
      color: 'hsl(221, 83%, 53%)'
    }
  ];

  const handleToolClick = (route: string) => {
    navigate(route);
  };

  return (
    <div className="p-6 bg-background/30 backdrop-blur-sm border border-border/30 rounded-lg">
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        className="flex items-center gap-3 mb-6"
      >
        <Settings className="w-5 h-5 text-primary" />
        <h2 className="text-lg font-semibold text-foreground">Available Tools</h2>
      </motion.div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {tools.map((tool, index) => (
          <motion.div
            key={tool.id}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: index * 0.1 }}
          >
            <Card 
              className="group cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-lg border border-border/50 bg-card/50 backdrop-blur-sm h-full"
              onClick={() => handleToolClick(tool.route)}
            >
              <CardContent className="p-8 text-center h-full flex flex-col justify-center">
                <div className="mb-6">
                  <div 
                    className="inline-flex p-4 rounded-2xl shadow-sm mx-auto"
                    style={{ 
                      backgroundColor: `${tool.color}15`,
                      border: `1px solid ${tool.color}30`
                    }}
                  >
                    <tool.icon 
                      className="w-8 h-8" 
                      style={{ color: tool.color }}
                    />
                  </div>
                </div>
                <div className="space-y-3">
                  <h3 className="text-lg font-semibold text-foreground group-hover:text-primary transition-colors">
                    {tool.title}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {tool.description}
                  </p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>
    </div>
  );
};